import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2, ShoppingBag } from "lucide-react";
import { api, resolveAsset } from "../lib/api";
import { useTheme } from "../context/ThemeContext";

/**
 * Public product grid rendered wherever the admin pastes `[[products]]`
 * (typically on the dedicated /page/amazon shop page). Pulls only
 * `active=true` products in sort order. Cards link out to the admin's
 * affiliate URL with `rel="sponsored noopener"` so search engines
 * understand the relationship.
 *
 * If the admin has defined product categories, a tab strip is rendered
 * above the grid so visitors can filter — "All" + one tab per category
 * (only categories that have at least one product show up). Tabs are
 * hidden entirely when there's just one bucket, so simple shops aren't
 * cluttered.
 */
export default function ProductGrid() {
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("__all__");
  const { theme } = useTheme();
  const disclosure = (theme?.affiliate_disclosure || "").trim();

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/products").catch(() => ({ data: [] })),
      api.get("/product-categories").catch(() => ({ data: [] })),
    ]).then(([pRes, cRes]) => {
      if (!alive) return;
      setProducts(pRes.data || []);
      setCategories(cRes.data || []);
    });
    return () => { alive = false; };
  }, []);

  // Which category buckets actually have at least one active product?
  // A product can sit in multiple buckets, so we flatten every product's
  // `category_ids` array into a single Set. Empty buckets are hidden so
  // the UI doesn't promise stuff that isn't there.
  const populatedCatIds = useMemo(() => {
    const s = new Set();
    for (const p of products || []) {
      const ids = Array.isArray(p.category_ids) ? p.category_ids : [];
      for (const id of ids) {
        if (id) s.add(id);
      }
    }
    return s;
  }, [products]);

  const hasUncategorised = useMemo(() => {
    const known = new Set(categories.map((c) => c.id));
    return (products || []).some((p) => {
      const ids = Array.isArray(p.category_ids) ? p.category_ids : [];
      const namedIds = ids.filter((id) => known.has(id));
      return namedIds.length === 0;
    });
  }, [products, categories]);

  const visibleCats = useMemo(() => {
    return (categories || []).filter((c) => populatedCatIds.has(c.id));
  }, [categories, populatedCatIds]);

  // Show tabs only when there's more than one bucket of products.
  const tabCount = visibleCats.length + (hasUncategorised ? 1 : 0);
  const showTabs = tabCount > 1;

  const filtered = useMemo(() => {
    if (!products) return null;
    if (active === "__all__") return products;
    if (active === "__none__") {
      const known = new Set(categories.map((c) => c.id));
      return products.filter((p) => {
        const ids = Array.isArray(p.category_ids) ? p.category_ids : [];
        const namedIds = ids.filter((id) => known.has(id));
        return namedIds.length === 0;
      });
    }
    // A product appears under every tab it belongs to.
    return products.filter(
      (p) => Array.isArray(p.category_ids) && p.category_ids.includes(active),
    );
  }, [products, active, categories]);

  if (products === null) {
    return (
      <div
        className="flex h-32 items-center justify-center gap-2 text-sm text-[var(--brand-body)]"
        data-testid="product-grid-loading"
      >
        <Loader2 size={14} className="animate-spin" /> Loading products…
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div
        className="my-6 rounded-md border border-dashed border-black/15 p-8 text-center"
        data-testid="product-grid-empty"
      >
        <ShoppingBag size={20} className="mx-auto text-[var(--brand-body)]" />
        <p className="mt-2 font-display text-sm font-bold">No products yet.</p>
        <p className="mt-1 text-xs text-[var(--brand-body)]">
          The admin can add some from Admin → Products.
        </p>
      </div>
    );
  }

  return (
    <div className="not-prose my-8" data-testid="product-grid">
      {showTabs ? (
        <div
          className="mb-6 flex flex-wrap gap-2"
          role="tablist"
          aria-label="Product categories"
          data-testid="product-category-tabs"
        >
          <CategoryTab
            label={`All (${products.length})`}
            isActive={active === "__all__"}
            onClick={() => setActive("__all__")}
            testid="product-tab-all"
          />
          {visibleCats.map((c) => {
            const count = products.filter(
              (p) => Array.isArray(p.category_ids) && p.category_ids.includes(c.id),
            ).length;
            return (
              <CategoryTab
                key={c.id}
                label={`${c.name} (${count})`}
                isActive={active === c.id}
                onClick={() => setActive(c.id)}
                testid={`product-tab-${c.slug}`}
              />
            );
          })}
          {hasUncategorised ? (
            <CategoryTab
              label="Other"
              isActive={active === "__none__"}
              onClick={() => setActive("__none__")}
              testid="product-tab-other"
            />
          ) : null}
        </div>
      ) : null}

      {filtered && filtered.length === 0 ? (
        <div
          className="my-6 rounded-md border border-dashed border-black/15 p-8 text-center"
          data-testid="product-grid-filtered-empty"
        >
          <p className="text-sm text-[var(--brand-body)]">
            Nothing in this category yet.
          </p>
        </div>
      ) : (
        <ul className="m-0 grid list-none grid-cols-1 gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </ul>
      )}

      {disclosure ? (
        <p
          className="mt-6 text-center text-xs text-[var(--brand-body)]"
          data-testid="affiliate-disclosure"
        >
          {disclosure}
        </p>
      ) : null}
    </div>
  );
}

function CategoryTab({ label, isActive, onClick, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center rounded-md border px-3.5 py-1.5 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
        isActive
          ? "border-[#0A0F17] bg-[#0A0F17] text-white"
          : "border-black/15 bg-white text-[var(--brand-ink)] hover:border-[#0A0F17]"
      }`}
    >
      {label}
    </button>
  );
}

function ProductCard({ product }) {
  const img = product.image_url ? resolveAsset(product.image_url) : "";
  // Plain HTML anchor props reused across the three semantic link targets
  // on each card (image, title, CTA). Amazon Associates requires every
  // affiliate destination to be a direct `<a href>` to amazon.* with no
  // JS click handlers, no link shorteners, and `rel="sponsored"`. No
  // onClick is attached anywhere on this card on purpose.
  const linkProps = {
    href: product.affiliate_url,
    target: "_blank",
    rel: "sponsored noopener noreferrer",
  };
  return (
    <li
      className="m-0 flex list-none flex-col overflow-hidden rounded-md border border-black/10 bg-white p-0 transition-shadow hover:shadow-md"
      data-testid={`product-card-${product.id}`}
    >
      <a
        {...linkProps}
        aria-label={`Buy ${product.title} on Amazon`}
        data-testid={`product-${product.id}-image-link`}
        className="block"
      >
        {img ? (
          <div className="relative aspect-[4/3] w-full bg-[#F8FAFC]">
            <img
              src={img}
              alt={product.title}
              loading="lazy"
              className="absolute inset-0 m-auto block h-full w-full object-contain p-4"
            />
          </div>
        ) : (
          <div className="flex aspect-[4/3] w-full items-center justify-center bg-[#F8FAFC]">
            <ShoppingBag size={28} className="text-[var(--brand-body)]" />
          </div>
        )}
      </a>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <h3 className="font-display text-base font-black tracking-tight">
            <a
              {...linkProps}
              data-testid={`product-${product.id}-title-link`}
              className="text-[var(--brand-ink)] no-underline hover:underline"
            >
              {product.title}
            </a>
          </h3>
          {product.description ? (
            <p className="mt-1 text-sm text-[var(--brand-body)] line-clamp-3">
              {product.description}
            </p>
          ) : null}
        </div>
        <div className="mt-auto flex items-center justify-between gap-3">
          {product.price ? (
            <p
              className="font-display text-lg font-black text-[var(--brand-ink)]"
              data-testid={`product-${product.id}-price`}
            >
              {product.price}
            </p>
          ) : <span />}
          <a
            {...linkProps}
            data-testid={`product-${product.id}-link`}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-2 font-display text-xs font-black uppercase tracking-wider text-white transition-colors hover:bg-[#FF9900] hover:text-[#0A0F17]"
          >
            {product.cta_label?.trim()
              || (product.vendor === "amazon"
                    ? "Buy on Amazon"
                    : product.vendor === "ebay"
                      ? "Buy on eBay"
                      : "Buy now")}
            <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </li>
  );
}
